/**
 * UI preferences (design + language) — per doctor account, with a device-level default for the
 * sign-in screens. Server sync is not available yet, so everything lives in this browser's storage.
 *
 * Keys (localStorage unless noted):
 * - `ui_design` / `app_language`   what this device shows right now; read at start-up by
 *                                  DesignContext / LanguageContext. Kept on logout, so the sign-in
 *                                  screen keeps the look that was last used.
 * - `ui_prefs_device`              the device default: choices made while signed out (sign-in
 *                                  screens). A doctor with no saved preference starts from it.
 * - `ui_prefs_doctor:<doctorId>`   one record per doctor account, written on every choice made while
 *                                  signed in and applied when that doctor signs in again.
 * - `ui_prefs_active_doctor`       whose record is applied in the current session (cleared on logout
 *                                  together with the rest of the session data).
 * - sessionStorage `ui_prefs_pre_login_choice`  fields chosen on the sign-in screens of this tab
 *                                  since the last sign-in; they win over the account record at
 *                                  sign-in, so a doctor who just picked English keeps English.
 *
 * A doctor never receives another doctor's record: at sign-in the order is
 * app default → device default → this doctor's own record → this tab's pre-login choice.
 *
 * The functions take the storages as arguments so they can be unit-tested without a browser.
 */

export type UiDesign = "legacy" | "v2";
export type UiLanguage = "ar" | "en";
export interface UiPrefs {
  design: UiDesign;
  language: UiLanguage;
}
export type UiPrefField = keyof UiPrefs;

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const DEFAULT_UI_PREFS: UiPrefs = { design: "legacy", language: "ar" };

export const DISPLAY_KEYS: Record<UiPrefField, string> = { design: "ui_design", language: "app_language" };
export const DEVICE_PREFS_KEY = "ui_prefs_device";
export const ACCOUNT_PREFS_PREFIX = "ui_prefs_doctor:";
export const ACTIVE_ACCOUNT_KEY = "ui_prefs_active_doctor";
export const PRE_LOGIN_CHOICE_KEY = "ui_prefs_pre_login_choice";

/** Window event carrying the preferences applied at sign-in (detail: UiPrefs). */
export const UI_PREFS_APPLIED_EVENT = "ui-preferences-applied";

export const accountPrefsKey = (doctorId: string) => `${ACCOUNT_PREFS_PREFIX}${doctorId}`;

/** localStorage keys that survive logout / an expired session (not session data). */
export function isPreservedOnLogout(key: string): boolean {
  return key === DISPLAY_KEYS.design || key === DISPLAY_KEYS.language || key === DEVICE_PREFS_KEY || key.startsWith(ACCOUNT_PREFS_PREFIX);
}

const isDesign = (v: unknown): v is UiDesign => v === "legacy" || v === "v2";
const isLanguage = (v: unknown): v is UiLanguage => v === "ar" || v === "en";

function safeGet(storage: KeyValueStorage | null | undefined, key: string): string | null {
  try {
    const v = storage?.getItem(key);
    return typeof v === "string" ? v : null;
  } catch {
    return null;
  }
}

function safeSet(storage: KeyValueStorage | null | undefined, key: string, value: string) {
  try {
    storage?.setItem(key, value);
  } catch {
    /* storage full or blocked — the choice still applies for this page */
  }
}

function safeRemove(storage: KeyValueStorage | null | undefined, key: string) {
  try {
    storage?.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Parses a stored record, keeping only valid fields. */
export function parsePrefs(raw: string | null): Partial<UiPrefs> {
  if (!raw) return {};
  try {
    const o = JSON.parse(raw);
    const out: Partial<UiPrefs> = {};
    if (o && isDesign(o.design)) out.design = o.design;
    if (o && isLanguage(o.language)) out.language = o.language;
    return out;
  } catch {
    return {};
  }
}

function writePrefs(storage: KeyValueStorage, key: string, prefs: Partial<UiPrefs>) {
  safeSet(storage, key, JSON.stringify(prefs));
}

/** What the device shows now (the start-up values). */
export function readDisplayPrefs(local: KeyValueStorage): UiPrefs {
  const design = safeGet(local, DISPLAY_KEYS.design);
  const language = safeGet(local, DISPLAY_KEYS.language);
  return {
    design: isDesign(design) ? design : DEFAULT_UI_PREFS.design,
    language: isLanguage(language) ? language : DEFAULT_UI_PREFS.language,
  };
}

function writeDisplayPrefs(local: KeyValueStorage, prefs: UiPrefs) {
  safeSet(local, DISPLAY_KEYS.design, prefs.design);
  safeSet(local, DISPLAY_KEYS.language, prefs.language);
}

/** Doctor id from a doctor access token (`user_id` / `sub` claim). No signature check — only a storage key. */
export function doctorIdFromToken(token: string | null | undefined): string | null {
  if (!token) return null;
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(padded));
    const id = json.user_id ?? json.sub;
    return id === undefined || id === null || id === "" ? null : String(id);
  } catch {
    return null;
  }
}

/** The doctor whose record is applied in this session, if someone is signed in. */
export function activeDoctorId(local: KeyValueStorage): string | null {
  const id = safeGet(local, ACTIVE_ACCOUNT_KEY);
  if (!id) return null;
  const signedIn = !!(safeGet(local, "access_token") || safeGet(local, "refresh_token"));
  return signedIn ? id : null;
}

/**
 * An explicit choice (toggle, menu, ?design= link). Signed in → the doctor's own record;
 * signed out → the device default and this tab's pre-login choice. Always → the display key.
 */
export function recordUiChoice<F extends UiPrefField>(
  field: F,
  value: UiPrefs[F],
  local: KeyValueStorage,
  session?: KeyValueStorage | null
): void {
  safeSet(local, DISPLAY_KEYS[field], value);
  const doctorId = activeDoctorId(local);
  if (doctorId) {
    const key = accountPrefsKey(doctorId);
    writePrefs(local, key, { ...parsePrefs(safeGet(local, key)), [field]: value });
    return;
  }
  writePrefs(local, DEVICE_PREFS_KEY, { ...parsePrefs(safeGet(local, DEVICE_PREFS_KEY)), [field]: value });
  if (session) writePrefs(session, PRE_LOGIN_CHOICE_KEY, { ...parsePrefs(safeGet(session, PRE_LOGIN_CHOICE_KEY)), [field]: value });
}

/** Preferences a doctor gets at sign-in (pure). */
export function resolveSignInPrefs(device: Partial<UiPrefs>, account: Partial<UiPrefs>, preLogin: Partial<UiPrefs>): UiPrefs {
  return { ...DEFAULT_UI_PREFS, ...device, ...account, ...preLogin };
}

export interface SignInOutcome {
  prefs: UiPrefs;
  /** The previous session belonged to another doctor (its session data must be purged). */
  switchedAccount: boolean;
  /** Nothing changed (same doctor, e.g. a token refresh). */
  unchanged: boolean;
}

/**
 * Called whenever tokens are stored. On a new sign-in (or a different doctor) the doctor's own
 * preferences are resolved, saved to their record and written to the display keys.
 */
export function activateAccountPrefs(doctorId: string, local: KeyValueStorage, session?: KeyValueStorage | null): SignInOutcome {
  const previous = safeGet(local, ACTIVE_ACCOUNT_KEY);
  if (previous === doctorId) {
    return { prefs: readDisplayPrefs(local), switchedAccount: false, unchanged: true };
  }
  const key = accountPrefsKey(doctorId);
  const prefs = resolveSignInPrefs(
    parsePrefs(safeGet(local, DEVICE_PREFS_KEY)),
    parsePrefs(safeGet(local, key)),
    parsePrefs(safeGet(session, PRE_LOGIN_CHOICE_KEY))
  );
  writePrefs(local, key, prefs);
  writeDisplayPrefs(local, prefs);
  safeSet(local, ACTIVE_ACCOUNT_KEY, doctorId);
  safeRemove(session, PRE_LOGIN_CHOICE_KEY);
  return { prefs, switchedAccount: !!previous && previous !== doctorId, unchanged: false };
}

/**
 * One-time upgrade from the single device-wide `ui_design` / `app_language`:
 * - the device default starts as what the device showed;
 * - a doctor who is already signed in keeps what they see as their own record.
 */
export function migrateUiPrefs(local: KeyValueStorage): void {
  const display = readDisplayPrefs(local);
  if (safeGet(local, DEVICE_PREFS_KEY) === null) {
    const seed: Partial<UiPrefs> = {};
    if (isDesign(safeGet(local, DISPLAY_KEYS.design))) seed.design = display.design;
    if (isLanguage(safeGet(local, DISPLAY_KEYS.language))) seed.language = display.language;
    writePrefs(local, DEVICE_PREFS_KEY, seed);
  }
  if (safeGet(local, ACTIVE_ACCOUNT_KEY) === null) {
    const doctorId = doctorIdFromToken(safeGet(local, "access_token")) || doctorIdFromToken(safeGet(local, "refresh_token"));
    if (doctorId) {
      const key = accountPrefsKey(doctorId);
      if (safeGet(local, key) === null) writePrefs(local, key, display);
      safeSet(local, ACTIVE_ACCOUNT_KEY, doctorId);
    }
  }
}
